import React, { useState, useEffect, useRef } from "react";
import {
  Filter,
  Download,
  RefreshCw,
  Play,
  Pause,
  Shield,
  // FileText,
  Activity,
  ChevronDown,
  FileText,
} from "lucide-react";
import {
  exportStreamReportCSV,
  exportStreamReportExcel,
  exportStreamReportPDF,
  getAllCaseNumbers,
  getAllCourtRooms,
  getProceedingDetails,
  getStreamReport,
} from "../../../api/reportAPI";
import logger from "../../../utils/logger";
import { formatDisplayDate, toISOForDB } from "../../../utils/Date";
import DateInput from "react-calendar-ui-kit";
import Pagination from "../../../utils/Pagination";

// Types
// interface ParticipantAction {
//   type: string;
//   actionBy?: string;
//   actionAt?: string;
//   reason?: string;
// }

// interface Participant {
//   userId: string;
//   name: string;
//   role?: string;
//   joinTime?: string;
//   leaveTime?: string;
//   actions: ParticipantAction[];
// }

interface StreamingRecord {
  _id: string;
  judgeName: string;
  courtRoom: string;
  cnr: string;
  hearingDate: string;
  actionPerformed: string;
  timestamp: string;
}

interface ActionRow {
  action: string;
  time: string | Date | null;
  reason: string;
  actionBy: string;
}

interface ParticipantActionRow {
  participant: string;
  time: string | Date | null;
  action: string;
  actionBy: string;
}

interface ViewData {
  streamingActions: ActionRow[];
  recordingActions: ActionRow[];
  participantActions: ParticipantActionRow[];
}

interface RoomOption {
  _id?: string;
  courtRoomNo: string;
  courtName: string;
}

interface Filters {
  from: string;
  to: string;
  actionType: string;
  courtroom: string;
  cnr: string;
}

const StreamingActivity: React.FC = () => {
  const [data, setData] = useState<StreamingRecord[]>([]);
  const [actionType, setActionType] = useState<string>("");
  const [roomOptions, setRoomOptions] = useState<RoomOption[]>([]);
  const [courtroom, setCourtroom] = useState<string>("");
  // const [caseNumber, setCaseNumber] = useState<string>("");
  const [cnrInput, setCnrInput] = useState("");
  const [filteredCnrs, setFilteredCnrs] = useState<string[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [cnr, setCnr] = useState("");

  const [from, setFrom] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [to, setTo] = useState<string>("");
  const [_, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [caseNumbersList, setCaseNumbersList] = useState<string[]>([]);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState<ViewData>({
    streamingActions: [],
    recordingActions: [],
    participantActions: [],
  });

  const [filters, setFilters] = useState<Filters>({
    from: "",
    to: "",
    actionType: "",
    courtroom: "",
    cnr: "",
  });
  const [totalCount, setTotalCount] = useState(0);
  const [pauseCount, setPauseCount] = useState(0);
  const [resumeCount, setResumeCount] = useState(0);
  const [overrideCount, setOverrideCount] = useState(0);

  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchCourtRooms();
    fetchCaseNumbers();
  }, []);
  useEffect(() => {
    if (!cnrInput) {
      setFilteredCnrs([]);
      return;
    }

    const filtered = caseNumbersList.filter((item) =>
      item.toLowerCase().includes(cnrInput.toLowerCase()),
    );

    setFilteredCnrs(filtered);
  }, [cnrInput, caseNumbersList]);

  useEffect(() => {
    fetchStreamingReports(filters, currentPage);
  }, [filters, currentPage]);

  const fetchCourtRooms = async () => {
    try {
      const res = await getAllCourtRooms();
      if (res.data?.status) {
        const mapped = res.data.data.map((r: any) => ({
          courtRoomNo: r._id,
          courtName:
            r.courtRoomNo + " - " + r.courtName ||
            r.courtRoomName ||
            r.roomNumber,
        }));
        setRoomOptions(mapped);
      } else setRoomOptions([]);
    } catch (err) {
      logger.error("fetchCourtRooms error:", err);
      setRoomOptions([]);
    }
  };

  const fetchCaseNumbers = async () => {
    try {
      const res = await getAllCaseNumbers();
      if (res.data?.status) setCaseNumbersList(res.data.data);
      else setCaseNumbersList([]);
    } catch (err) {
      logger.error("fetchCaseNumbers error:", err);
      setCaseNumbersList([]);
    }
  };

  // const fetchStreamingReports = async (filters: Filters, page: number) => {
  //   setLoading(true);
  //   try {
  //     const res = await getStreamReport({
  //       from: filters.from,
  //       to: filters.to,
  //       actionType: filters.actionType,
  //       courtroom: filters.courtroom,
  //       cnr,
  //       page,
  //       limit: 10,
  //     });
  //     if (res.data?.status && Array.isArray(res.data.data)) {
  //       setData(res.data.data);
  //       setTotalPages(res.data.meta?.totalPages || 1);
  //     } else {
  //       setData([]);
  //       setTotalPages(1);
  //     }
  //   } catch (err) {
  //     logger.error("fetchStreamingReports error:", err);
  //     setData([]);
  //     setTotalPages(1);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchStreamingReports = async (filters: Filters, page: number) => {
    setLoading(true);
    try {
      const isFiltering =
        filters.from ||
        filters.to ||
        filters.actionType ||
        filters.courtroom ||
        cnr;

      const payload: any = {
        from: filters.from,
        to: filters.to,
        actionType: filters.actionType,
        courtroom: filters.courtroom,
        cnr,
      };

      // ✅ Only send pagination when NOT filtering
      if (!isFiltering) {
        payload.page = page;
        payload.limit = 10;
      }

      const res = await getStreamReport(payload);
      logger.log("check response", res?.data?.data);

      if (res.data?.status && Array.isArray(res.data.data)) {
        //  logger.log("Raw hearing dates:", res.data.data.map((i: any) => i.hearingDate));

        setData(res.data.data);
        setTotalCount(res.data.meta?.total || 0);
        setPauseCount(res.data.meta?.pauseCount || 0);
        setResumeCount(res.data.meta?.resumeCount || 0);
        setOverrideCount(res.data.meta?.overrideCount || 0);

        if (!isFiltering) {
          setTotalPages(res.data.meta?.totalPages || 1);
        } else {
          setTotalPages(1);
        }
      } else {
        setData([]);
        setTotalPages(1);
      }
    } catch (err) {
      logger.error("fetchStreamingReports error:", err);
      setData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  const handleDateInputValidation = (
    value: string,
    setter: (v: string) => void,
  ) => {
    if (!value) return setter("");

    const isoValue = toISOForDB(value) ?? "";

    if (setter === setFrom && to && new Date(isoValue) > new Date(to)) {
      setTo(isoValue);
    }

    if (setter === setTo && from && new Date(isoValue) < new Date(from)) {
      setFrom(isoValue);
    }

    setter(isoValue);
  };

  // const handleDateInputValidation = (
  //   value: string,
  //   setter: React.Dispatch<React.SetStateAction<string>>,
  // ) => {

  //   const year = value.split("-")[0];
  //   if (year.length > 4) {
  //     alert("Year must be only 4 digits.");
  //     return;
  //   }
  //   setter(value);
  // };

  // const handleReset = () => {
  //   setCourtroom("");
  //   setCnr("");
  //   setActionType("");
  //   setFrom("");
  //   setTo("");
  //   setFilters({
  //     from: "",
  //     to: "",
  //     actionType: "",
  //     courtroom: "",
  //     cnr: "",
  //   });
  //   setCurrentPage(1);
  // };
  const handleReset = async () => {
    try {
      setResetLoading(true);

      setCourtroom("");
      setCnr("");
      setCnrInput("");
      setFilteredCnrs([]);
      setActionType("");
      setFrom("");
      setTo("");
      setFilters({
        from: "",
        to: "",
        actionType: "",
        courtroom: "",
        cnr: "",
      });
    } catch (error) {
      logger.error("Reset error:", error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleFilter = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (from && to && new Date(from) > new Date(to)) {
      alert("From date cannot be greater than To date.");
      return;
    }
    try {
      setFilterLoading(true);

      setFilters({
        from: from,
        to,
        actionType,
        courtroom,
        cnr,
      });
      setCurrentPage(1);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleExport = async (type: "csv" | "excel" | "pdf") => {
    try {
      setExportLoading(true);
      const params = {
        from: filters.from,
        to: filters.to,
        actionType: filters.actionType,
        courtroom: filters.courtroom,
        caseNumber: filters.cnr,
      };

      let response;
      if (type === "csv") response = await exportStreamReportCSV(params);
      else if (type === "excel")
        response = await exportStreamReportExcel(params);
      else response = await exportStreamReportPDF(params);

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().split("T")[0];
      a.download = `stream_report_${formatDisplayDate(today)}.${type === "excel" ? "xlsx" : type}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("Export failed:", err);
      alert("Failed to export report.");
    } finally {
      setExportLoading(false);
    }
  };
  const handleView = async (id: string) => {
    try {
      const res = await getProceedingDetails(id);

      if (res.data?.status && res.data.data) {
        const d = res.data.data;

        setViewData({
          streamingActions: Array.isArray(d.streamingActions)
            ? d.streamingActions
            : [],
          recordingActions: Array.isArray(d.recordingActions)
            ? d.recordingActions
            : [],
          participantActions: Array.isArray(d.participantActions)
            ? d.participantActions
            : [],
        });

        setShowViewModal(true);
      }
    } catch (err) {
      logger.error("View Error:", err);
    }
  };

  return (
    <div className="min-h-screen max-w-screen w-full p-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                From Date
              </label>
              <DateInput
                value={formatDisplayDate(from) ?? ""}
                onChange={(date: string) =>
                  handleDateInputValidation(date, setFrom)
                }
                theme={{
                  selectWidth: "140px",
                  yearButtonWidth: "70px",
                }}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                To Date
              </label>
              <DateInput
                value={formatDisplayDate(to) ?? ""}
                onChange={(date: string) =>
                  handleDateInputValidation(date, setTo)
                }
                theme={{
                  selectWidth: "140px",
                  yearButtonWidth: "70px",
                }}
              />
            </div>

            {/* <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Action Type
              </label>
              <div className="relative">
                <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                >
                  <option value="">All Actions</option>
                  <option value="pause">Pause</option>
                  <option value="resume">Resume</option>
                  <option value="override">Override</option>
                </select>
              </div>
            </div> */}

            <div className="space-y-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700">
                Courtroom
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-full min-h-[42px] px-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-left"
                >
                  <span className="block break-words whitespace-normal leading-relaxed text-sm text-gray-900">
                    {courtroom
                      ? roomOptions.find((r) => r.courtRoomNo === courtroom)
                          ?.courtName || "All Rooms"
                      : "All Rooms"}
                  </span>
                </button>

                <ChevronDown
                  className={`absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""}`}
                />

                {isOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div
                      onClick={() => {
                        setCourtroom("");
                        setIsOpen(false);
                      }}
                      className="px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors"
                    >
                      <p className="text-sm text-gray-900">All Rooms</p>
                    </div>

                    {roomOptions.map((room, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setCourtroom(room.courtRoomNo);
                          setIsOpen(false);
                        }}
                        className={`px-4 py-3 hover:bg-indigo-50 cursor-pointer transition-colors ${
                          courtroom === room.courtRoomNo ? "bg-indigo-100" : ""
                        }`}
                      >
                        <p className="text-sm text-gray-900 break-words whitespace-normal leading-relaxed">
                          {room.courtName}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                CNR
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={cnr}
                  onChange={(e) => setCnr(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                >
                  <option value="">Select CNR</option>
                  {caseNumbersList.map((cnrValue, idx) => (
                    <option key={idx} value={cnrValue}>
                      {cnrValue}
                    </option>
                  ))}
                </select>
              </div>
            </div> */}
            <div className="space-y-2 relative">
              <label className="block text-sm font-medium text-gray-700">
                CNR
              </label>

              <input
                type="text"
                value={cnrInput}
                onChange={(e) => {
                  setCnrInput(e.target.value);
                  setCnr(e.target.value); // filter ke liye
                }}
                placeholder="Type CNR..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />

              {filteredCnrs.length > 0 && (
                <ul className="absolute z-20 bg-white border w-full max-h-48 overflow-y-auto rounded-md shadow">
                  {filteredCnrs.map((item, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        setCnr(item);
                        setCnrInput(item);
                        setFilteredCnrs([]);
                      }}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-4 ">
            <div className="flex flex-col  sm:flex-row items-start sm:items-center space-x-3">
              <button
                onClick={handleFilter}
                disabled={filterLoading}
                className="xs:w-4/5 w-full  inline-flex items-center px-6 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50"
              >
                {filterLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Filter className="w-4 h-4 mr-2" />
                )}
                {filterLoading ? "Applying..." : "Apply Filters"}
              </button>

              <button
                onClick={handleReset}
                disabled={resetLoading}
                className={` sm:mt-0 mt-3 inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors
                  ${resetLoading ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${resetLoading ? "animate-spin" : ""}`}
                />
                Reset
              </button>
            </div>

            <div className="relative">
              <button
                type="button"
                disabled={exportLoading}
                onClick={() => setShowExportDropdown((prev) => !prev)}
                className="max-w-48 inline-flex items-center justify-center gap-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {exportLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportLoading ? "Exporting..." : "Export Report"}
              </button>

              {showExportDropdown && (
                <div className=" absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleExport("csv")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as CSV
                  </button>
                  <button
                    onClick={() => handleExport("excel")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as Excel
                  </button>
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Activities
              </p>
              <p className="text-3xl font-bold text-gray-900">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pause Actions</p>
              <p className="text-3xl font-bold text-red-600">{pauseCount}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <Pause className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Resume Actions
              </p>
              <p className="text-3xl font-bold text-green-600">{resumeCount}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Play className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Override Actions
              </p>
              <p className="text-3xl font-bold text-orange-600">
                {overrideCount}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Shield className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Streaming Activities
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Judge Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courtroom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CNR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hearing Date
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.length > 0 ? (
                data.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(currentPage - 1) * 10 + idx + 1}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.judgeName || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.courtRoom || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.cnr || "—"}
                    </td>

                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(item.actionPerformed)}
                    </td> */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleView(item?._id)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                      >
                        View
                      </button>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {formatDisplayDate(item.hearingDate) || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center">
                      <FileText className="w-12 h-12 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-600 mb-0">
                        No streaming activities found
                      </h3>
                      <p className="text-xs text-gray-400">
                        Try adjusting your filters to see more results
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
      />
      {showViewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-[999]">
          <div className="bg-white shadow-xl border border-gray-200 w-full max-w-5xl rounded-2xl overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
              <h2 className="text-xl font-bold text-white">
                Proceeding Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-white text-lg font-semibold hover:text-red-200"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 max-h-[80vh] overflow-y-auto space-y-8">
              {/* Streaming Actions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  1. Streaming Actions
                </h3>
                <div className="overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr className="text-gray-700">
                        <th className="p-3 border">Action</th>
                        <th className="p-3 border">Time</th>
                        <th className="p-3 border">Reason</th>
                        <th className="p-3 border">Action By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewData.streamingActions?.length ? (
                        viewData.streamingActions.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="p-2 border">{row?.action || "—"}</td>
                            <td className="p-2 border">
                              {row?.time
                                ? new Date(row.time).toLocaleString()
                                : "—"}
                            </td>
                            <td className="p-2 border">
                              {row?.reason?.length ? row.reason : "—"}
                            </td>
                            <td className="p-2 border">
                              {row?.actionBy || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-4 text-center text-gray-500"
                          >
                            No Streaming Actions Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recording Actions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  2. Recording Actions
                </h3>
                <div className="overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr className="text-gray-700">
                        <th className="p-3 border">Action</th>
                        <th className="p-3 border">Time</th>
                        <th className="p-3 border">Reason</th>
                        <th className="p-3 border">Action By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewData.recordingActions?.length ? (
                        viewData.recordingActions.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="p-2 border">{row?.action || "—"}</td>
                            <td className="p-2 border">
                              {row?.time
                                ? new Date(row.time).toLocaleString()
                                : "—"}
                            </td>
                            <td className="p-2 border">{row?.reason || "—"}</td>
                            <td className="p-2 border">
                              {row?.actionBy || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-4 text-center text-gray-500"
                          >
                            No Recording Actions Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Participant Actions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  3. Participant Actions
                </h3>
                <div className="overflow-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr className="text-gray-700">
                        <th className="p-3 border">Participant</th>
                        <th className="p-3 border">Time</th>
                        <th className="p-3 border">Action</th>
                        <th className="p-3 border">Action By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewData.participantActions?.length ? (
                        viewData.participantActions.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="p-2 border">
                              {row?.participant || "—"}
                            </td>
                            <td className="p-2 border">
                              {row?.time
                                ? new Date(row.time).toLocaleString()
                                : "—"}
                            </td>
                            <td className="p-2">{row?.action || "—"}</td>
                            <td className="p-2 border">
                              {row?.actionBy || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-4 text-center text-gray-500"
                          >
                            No Participant Actions Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 text-right">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamingActivity;
